import { defineStore } from 'pinia';

export interface ChunkState {
  index: number;
  start: number;
  end: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
}

interface UploadState {
  fileId: string | null;
  fileName: string | null;
  fileSize: number;
  totalChunks: number;
  chunks: ChunkState[];
  overallProgress: number;
  uploadStatus: 'idle' | 'uploading' | 'paused' | 'completed' | 'failed';
}

export const useUploadStore = defineStore('upload', {
  state: (): UploadState => ({
    fileId: null,
    fileName: null,
    fileSize: 0,
    totalChunks: 0,
    chunks: [],
    overallProgress: 0,
    uploadStatus: 'idle',
  }),

  actions: {
    initializeUpload(file: File) {
      const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      
      this.fileId = crypto.randomUUID();
      this.fileName = file.name;
      this.fileSize = file.size;
      this.totalChunks = totalChunks;
      this.chunks = Array.from({ length: totalChunks }, (_, index) => ({
        index,
        start: index * CHUNK_SIZE,
        end: Math.min((index + 1) * CHUNK_SIZE, file.size),
        status: 'pending',
        progress: 0,
      }));
      this.uploadStatus = 'idle';
      this.overallProgress = 0;

      // 保存上传状态到 localStorage
      this.saveState();
    },

    updateChunkProgress(chunkIndex: number, progress: number) {
      if (this.chunks[chunkIndex]) {
        this.chunks[chunkIndex].progress = progress;
        this.calculateOverallProgress();
        this.saveState();
      }
    },

    updateChunkStatus(chunkIndex: number, status: ChunkState['status']) {
      if (this.chunks[chunkIndex]) {
        this.chunks[chunkIndex].status = status;
        this.saveState();
      }
    },

    calculateOverallProgress() {
      const totalProgress = this.chunks.reduce((sum, chunk) => sum + chunk.progress, 0);
      this.overallProgress = totalProgress / this.totalChunks;
    },

    setUploadStatus(status: UploadState['uploadStatus']) {
      this.uploadStatus = status;
      this.saveState();
    },

    saveState() {
      if (this.fileId) {
        localStorage.setItem(`upload_state_${this.fileId}`, JSON.stringify({
          fileId: this.fileId,
          fileName: this.fileName,
          fileSize: this.fileSize,
          totalChunks: this.totalChunks,
          chunks: this.chunks,
          uploadStatus: this.uploadStatus,
        }));
      }
    },

    loadState(fileId: string): boolean {
      const savedState = localStorage.getItem(`upload_state_${fileId}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        Object.assign(this, state);
        return true;
      }
      return false;
    },

    clearState() {
      if (this.fileId) {
        localStorage.removeItem(`upload_state_${this.fileId}`);
      }
      this.$reset();
    },

    getPendingChunks(): ChunkState[] {
      return this.chunks.filter(chunk => 
        chunk.status === 'pending' || chunk.status === 'failed'
      );
    },

    isUploadComplete(): boolean {
      return this.chunks.every(chunk => chunk.status === 'completed');
    }
  },
}); 